import { Injectable, Injector } from '@angular/core';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { AppInsightsService, TelemetryProperties, TelemetryScope } from '@app/core/services/app-insights.service';
import { RemoteErrorLoggingService } from '@app/shared/handlers/remote-error-logging.service';
import { renderMessageTemplate } from '@app/shared/utils/message-template';
import { env, LogLevel } from 'src/environments/env';

const LEVELS: Record<LogLevel, { rank: number; severity: SeverityLevel }> = {
  verbose: { rank: 0, severity: SeverityLevel.Verbose },
  debug: { rank: 1, severity: SeverityLevel.Verbose },
  information: { rank: 2, severity: SeverityLevel.Information },
  warning: { rank: 3, severity: SeverityLevel.Warning },
  error: { rank: 4, severity: SeverityLevel.Error },
  fatal: { rank: 5, severity: SeverityLevel.Critical },
};

/** Serilog-style logger: message templates, `forContext` child loggers and ambient `pushProperty` scopes. */
@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  private properties: TelemetryProperties = {};
  private readonly minRank = LEVELS[env.get().appInsights.minLogLevel ?? 'information'].rank;

  constructor(
    private readonly appInsights: AppInsightsService,
    // Resolved lazily so consumers don't pull in HttpClient unless an error is logged.
    private readonly injector: Injector,
  ) {}

  /** Child logger with properties bound to everything it writes. Safe across `await`, unlike `pushProperty`. */
  forContext(properties: TelemetryProperties): LoggingService {
    const child = new LoggingService(this.appInsights, this.injector);
    child.properties = { ...this.properties, ...properties };
    return child;
  }

  /** Adds a property to all telemetry until disposed. App-wide, so use for UI-scoped context only. */
  pushProperty(name: string, value: unknown): TelemetryScope {
    return this.appInsights.pushProperties({ [name]: value });
  }

  pushProperties(properties: TelemetryProperties): TelemetryScope {
    return this.appInsights.pushProperties(properties);
  }

  setProperty(name: string, value: unknown): void {
    this.appInsights.setProperty(name, value);
  }

  verbose(template: string, ...args: unknown[]): void {
    this.write('verbose', template, args);
  }

  debug(template: string, ...args: unknown[]): void {
    this.write('debug', template, args);
  }

  information(template: string, ...args: unknown[]): void {
    this.write('information', template, args);
  }

  warning(template: string, ...args: unknown[]): void {
    this.write('warning', template, args);
  }

  /** Also reported to the backend error endpoint, as before. */
  error(error: Error | string, template?: string, ...args: unknown[]): void {
    this.writeException('error', error, template, args);
  }

  fatal(error: Error | string, template?: string, ...args: unknown[]): void {
    this.writeException('fatal', error, template, args);
  }

  event(name: string, properties?: TelemetryProperties): void {
    this.appInsights.trackEvent(name, { ...this.properties, ...properties });
  }

  private write(level: LogLevel, template: string, args: unknown[]): void {
    if (LEVELS[level].rank < this.minRank) {
      return;
    }
    const { message, properties } = renderMessageTemplate(template, args);
    this.appInsights.trackTrace(message, LEVELS[level].severity, {
      ...this.properties,
      ...properties,
      MessageTemplate: template,
    });
  }

  private writeException(level: LogLevel, error: Error | string, template: string | undefined, args: unknown[]): void {
    void this.injector.get(RemoteErrorLoggingService).logError(error);

    const rendered = template ? renderMessageTemplate(template, args) : { message: undefined, properties: {} };
    this.appInsights.trackException(
      error instanceof Error ? error : new Error(String(error)),
      { ...this.properties, ...rendered.properties, MessageTemplate: template, Message: rendered.message },
      LEVELS[level].severity,
    );
  }
}
